import { useEffect, useMemo } from 'react'
import styles from './PifOverlay.module.css'
import classNames from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { PhotonInput } from '@/atoms/PhotonInput'
import { CaptionSM } from '@/atoms/Text'
import { PIF_NAME_IN_CREDITS_MIN_USD } from '@/constants/pif'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { useTranslate } from '@/utils/translate/translate-client'
import { PerkStarIcon } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/PerkStarIcon'
import { TheatricalExpressPriceOptions } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressPriceOptions'
import { TheatricalPifCheckoutWithSubtotal } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalPifCheckoutWithSubtotal'
import { usePifOverlay } from '../contexts/PifOverlayContext'
import { DavidGoalProgress } from './DavidGoalProgress'

interface PifOverlayProps {
  releaseDate?: string
  projectSlug: string
}

export function PifOverlay({ releaseDate, projectSlug }: PifOverlayProps) {
  const { t } = useTranslate('pif')
  const { isOpen, closeOverlay } = usePifOverlay()
  const { offersData, selectedOffer, handleSetSelectedOffer, nameInCredits, setNameInCredits } =
    usePayItForwardOfferData()

  // Calculate tickets and credits eligibility based on selected offer
  const ticketsProvided = selectedOffer
    ? Math.floor(getPriceForCurrency(selectedOffer.price, selectedOffer.currency) / 15)
    : 0
  const showCredits = selectedOffer
    ? getPriceForCurrency(selectedOffer.price, selectedOffer.currency) >= PIF_NAME_IN_CREDITS_MIN_USD
    : false

  // Filter offers to only show those <= $7500
  const filteredOffers = useMemo(() => {
    return offersData?.filter((offer) => getPriceForCurrency(offer.price, offer.currency) <= 7500) || []
  }, [offersData])

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!selectedOffer) return null

  const selectedPrice = getPriceForCurrency(selectedOffer.price, selectedOffer.currency)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - only visible on desktop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 hidden bg-black/30 md:block"
            onClick={closeOverlay}
          />

          {/* Overlay Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
            }}
            className={styles.overlay}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Layout */}
            <div className={styles.mobileLayout}>
              {/* Fixed Header */}
              <div className="shrink-0">
                <div className="relative w-full shrink-0 md:hidden">
                  <div className="flex size-full flex-row items-center">
                    <div className="relative flex w-full content-stretch items-center px-[24px] pb-[12px] pt-[24px]">
                      <button
                        onClick={closeOverlay}
                        className="relative block size-[32px] shrink-0 cursor-pointer p-1"
                        aria-label="Close"
                      >
                        <svg
                          className="block size-full"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className={styles.scrollableContent}>
                <div className="relative flex flex-col gap-[24px] px-[24px] py-[16px]">
                  {/* Header */}
                  <div className="font-angel-royal relative flex w-full shrink-0 items-start justify-between whitespace-pre text-nowrap text-[40px] lowercase leading-[32px] md:text-[48px] md:leading-[40px]">
                    <p className="relative shrink-0">{t('payItForward', 'Pay it Forward')}</p>
                    <p className="relative shrink-0">${selectedPrice}</p>
                  </div>

                  {/* Description */}
                  <p className="font-whitney-medium relative w-full shrink-0 text-[14px] not-italic leading-[26px] text-[#411e09] md:text-[16px] md:leading-[28px]">
                    {t(
                      'davidPifOverlayDescription',
                      'Give a child the chance to see DAVID on the big screen—for free.',
                    )}
                  </p>

                  {/* Progress */}
                  <DavidGoalProgress
                    releaseDate={releaseDate}
                    textColor="#411e09"
                    progressBarBg="bg-white"
                    progressBarBorder="border-[#f0e0d3]"
                    progressBarFillBg="bg-[#ea6f13]"
                    textSize="sm"
                    shouldAnimateOnScroll={false}
                    showGoalLineTitle={false}
                  />

                  {/* Price Options */}
                  <div className="relative w-full shrink-0">
                    <TheatricalExpressPriceOptions
                      offers={filteredOffers}
                      onClick={handleSetSelectedOffer}
                      selectedOffer={selectedOffer}
                      shouldShowPeopleImpacted={false}
                      shouldUseEqualColumns
                      newPifLayout
                      pifNameInCreditsEnabled
                    />
                  </div>

                  {/* Impact */}
                  <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[8px]">
                    <p className="font-angel-royal relative w-full shrink-0 text-[32px] lowercase not-italic leading-[24px] text-[#411e09]">
                      {t('yourImpact', 'Your Impact')}
                    </p>
                    <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[8px]">
                      {/* Tickets Impact */}
                      <div className="relative flex w-full shrink-0 content-stretch items-center gap-[4px]">
                        <CircleCheckIcon size={24} color1="david-orange" className="relative shrink-0" />
                        <div className="relative flex shrink-0 content-stretch items-center gap-[4px] whitespace-pre text-nowrap text-[14px] leading-[18px] text-black">
                          <p className="font-whitney-bold relative shrink-0 not-italic">
                            {t('payItForward', 'Pay it Forward')}
                          </p>
                          <p className="font-whitney-medium-italic relative shrink-0 italic">
                            ({ticketsProvided} {t('tickets', 'Tickets')})
                          </p>
                        </div>
                      </div>

                      {/* Name in Credits */}
                      <div
                        className={classNames('relative flex w-full shrink-0 content-stretch items-center gap-[4px]', {
                          'opacity-30': !showCredits,
                        })}
                      >
                        <PerkStarIcon size={24} color="warm-03" className="relative shrink-0" />
                        <div className="relative flex shrink-0 content-stretch items-center gap-[4px] whitespace-pre text-nowrap text-[14px] leading-[18px] text-black">
                          <p className="font-whitney-bold relative shrink-0 not-italic">
                            {t('yourNameInCredits', 'Your Name in the Credits')}
                          </p>
                          {!showCredits && (
                            <p className="font-whitney-medium-italic relative shrink-0 italic">
                              ({t('dollarAmountOrMore', '${{amount}} or more', { amount: PIF_NAME_IN_CREDITS_MIN_USD })}
                              )
                            </p>
                          )}
                        </div>
                      </div>
                      {showCredits && (
                        <div className="mt-2 flex w-[90%] flex-col gap-2 self-end">
                          <PhotonInput
                            name="nameInCredits"
                            label={t('nameInCreditsLabel', 'Enter your name for the credits')}
                            placeholder="Your name here"
                            minLength={3}
                            maxLength={40}
                            value={nameInCredits}
                            onChange={(e) => setNameInCredits(e.target.value)}
                          />
                          <CaptionSM color="core-warning-800" italic>
                            {t('personalOrFamilyNamesOnlyPlease', 'Personal or family names only please.')}
                          </CaptionSM>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Bottom Button */}
              <div className={styles.bottomButton}>
                <div className="relative flex w-full flex-col content-stretch items-start gap-[8px] p-[16px]">
                  <TheatricalPifCheckoutWithSubtotal
                    projectSlug={projectSlug}
                    selectedOffer={selectedOffer}
                    isSubscription={false}
                    newPifLayout
                    pifNameInCreditsEnabled
                    overlayStyle
                  />
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className={styles.desktopLayout}>
              <div className="size-full">
                <div className="relative flex size-full flex-col content-stretch items-start gap-[24px] p-[40px]">
                  {/* Desktop Header with Close */}
                  <div className="relative hidden w-full shrink-0 md:block">
                    <div className="flex size-full flex-row items-center">
                      <div className="relative flex w-full content-stretch items-center py-0 pl-0 pr-[32px]">
                        <button
                          onClick={closeOverlay}
                          className="relative block size-[32px] shrink-0 cursor-pointer p-1"
                          aria-label="Close"
                        >
                          <svg
                            className="block size-full"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative flex min-h-px min-w-px grow basis-0 flex-col items-start gap-[16px] md:gap-[24px]">
                    {/* Header */}
                    <div className="font-angel-royal relative flex w-full shrink-0 items-start justify-between whitespace-pre text-nowrap text-[40px] lowercase leading-[32px] md:text-[48px] md:leading-[40px]">
                      <p className="relative shrink-0">{t('payItForward', 'Pay it Forward')}</p>
                      <p className="relative shrink-0">${selectedPrice}</p>
                    </div>

                    {/* Description */}
                    <p className="font-whitney-medium relative w-full shrink-0 text-[14px] not-italic leading-[26px] text-[#411e09] md:text-[16px] md:leading-[28px]">
                      {t(
                        'davidPifOverlayDescription',
                        'Give a child the chance to see DAVID on the big screen—for free.',
                      )}
                    </p>

                    {/* Progress */}
                    <DavidGoalProgress
                      releaseDate={releaseDate}
                      textColor="#411e09"
                      progressBarBg="bg-white"
                      progressBarBorder="border-[#f0e0d3]"
                      progressBarFillBg="bg-[#ea6f13]"
                      textSize="sm"
                      shouldAnimateOnScroll={false}
                      showGoalLineTitle={false}
                    />

                    {/* Price Options */}
                    <div className="relative w-full shrink-0">
                      <TheatricalExpressPriceOptions
                        offers={filteredOffers}
                        onClick={handleSetSelectedOffer}
                        selectedOffer={selectedOffer}
                        shouldShowPeopleImpacted={false}
                        shouldUseEqualColumns
                        newPifLayout
                        pifNameInCreditsEnabled
                      />
                    </div>

                    {/* Impact */}
                    <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[8px]">
                      <p className="font-angel-royal relative w-full shrink-0 text-[32px] lowercase not-italic leading-[24px] text-[#411e09]">
                        {t('yourImpact', 'Your Impact')}
                      </p>
                      <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[8px]">
                        {/* Tickets Impact */}
                        <div className="relative flex w-full shrink-0 content-stretch items-center gap-[4px]">
                          <CircleCheckIcon size={24} color1="david-orange" className="relative shrink-0" />
                          <div className="relative flex shrink-0 content-stretch items-center gap-[4px] whitespace-pre text-nowrap text-[14px] leading-[18px] text-black">
                            <p className="font-whitney-bold relative shrink-0 not-italic">
                              {t('payItForward', 'Pay it Forward')}
                            </p>
                            <p className="font-whitney-medium-italic relative shrink-0 italic">
                              ({ticketsProvided} {t('tickets', 'Tickets')})
                            </p>
                          </div>
                        </div>

                        {/* Name in Credits */}
                        <div
                          className={classNames(
                            'relative flex w-full shrink-0 content-stretch items-center gap-[4px]',
                            {
                              'opacity-30': !showCredits,
                            },
                          )}
                        >
                          <PerkStarIcon size={24} color="warm-03" className="relative shrink-0" />
                          <div className="relative flex shrink-0 content-stretch items-center gap-[4px] whitespace-pre text-nowrap text-[14px] leading-[18px] text-black">
                            <p className="font-whitney-bold relative shrink-0 not-italic">
                              {t('yourNameInCredits', 'Your Name in the Credits')}
                            </p>
                            {!showCredits && (
                              <p className="font-whitney-medium-italic relative shrink-0 italic">
                                (
                                {t('dollarAmountOrMore', '${{amount}} or more', {
                                  amount: PIF_NAME_IN_CREDITS_MIN_USD,
                                })}
                                )
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      {showCredits && (
                        <div className="mt-2 flex w-[90%] flex-col gap-2 self-end">
                          <PhotonInput
                            name="nameInCredits"
                            label={t('nameInCreditsLabel', 'Enter your name for the credits')}
                            placeholder="Your name here"
                            minLength={3}
                            maxLength={40}
                            value={nameInCredits}
                            onChange={(e) => setNameInCredits(e.target.value)}
                          />
                          <CaptionSM color="core-warning-800" italic>
                            {t('personalOrFamilyNamesOnlyPlease', 'Personal or family names only please.')}
                          </CaptionSM>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <TheatricalPifCheckoutWithSubtotal
                    projectSlug={projectSlug}
                    selectedOffer={selectedOffer}
                    isSubscription={false}
                    newPifLayout
                    pifNameInCreditsEnabled
                    overlayStyle
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
